import React, { ReactElement } from 'react';
import { MenuItem, SelectProps } from '@material-ui/core';
import classNames from 'classnames';
import { StandardInputWrapper } from '@components/StatefulInputs/Base/StandardInputWrapper/StandardInputWrapper';
import { FieldValues, useController } from 'react-hook-form';
import { makeStyles } from '@material-ui/core/styles';
import { ChatAwareSelect } from '@components/ChatAwareComponents/ChatAwareSelect/ChatAwareSelect';
import { StatefulSelectProps, SelectOption, SelectOptionCompatible } from './StatefulSelect.types';
import { mergeUrl } from '@utilities/mergeUrl';

const useStyles = makeStyles((theme) => ({
	fancyScrollOptions: {
		'& .MuiPaper-root': {
			'&::-webkit-scrollbar': {
				width: theme.spacing(1),
				height: theme.spacing(1),
				color: theme.palette.primary[400],
			},
			'&::-webkit-scrollbar-track:horizontal': { backgroundRepeat: 'repeat-x' },
			'&::-webkit-scrollbar-track:vertical': { backgroundRepeat: 'repeat-y' },
			'&::-webkit-scrollbar-thumb': {
				backgroundColor: theme.palette.primary[400],
				borderRadius: theme.spacing(0.5),
			},
			'&::-webkit-scrollbar-track': {
				backgroundImage: `url(${mergeUrl('/assets/gray.png')})`,
				backgroundColor: 'transparent',
				backgroundPosition: 'center',
			},
		},
	},
	placeholder: {
		color: theme.palette.grey[600],
	}
}));

/**
 * `onBeforeChange` is an optional callback function that is invoked before the state of the select input is changed.
 * This function receives the change event as its argument.
 *
 * The function should return a boolean value:
 * - If it returns `true`, the select input's state will be updated with the new value.
 * - If it returns `false`, the select input's state will not be updated, effectively preventing the change.
 *
 * This can be useful when you want to perform validation or other checks before allowing the state to change.
 *
 * @example
 * <StatefulSelect
 *   onBeforeChange={(event) => {
 *     // Prevent the state from changing if the new value is 'invalid'
 *     if (event.target.value === 'invalid') {
 *       return false;
 *     }
 *     return true;
 *   }}
 *   // other props...
 * />
 **/

export function StatefulSelect<
	FormType extends FieldValues,
	OptionType extends SelectOptionCompatible = SelectOption
>({
	options,
	controllerOptions,
	onBeforeChange,
	placeholder,
	MenuProps,
	value,
	...statefulProviderProps
}: StatefulSelectProps<FormType, OptionType> &
	Pick<SelectProps, 'placeholder' | 'MenuProps' | 'value' | 'IconComponent'> & {
	useFancyScrollStyle?: boolean;
}): ReactElement {
	const { field } = useController<FormType>({
		name: statefulProviderProps.name,
		...controllerOptions,
	});

	const selectValue = value || field.value;
	const classes = useStyles();

	const handleChange = React.useCallback(
		(event: React.ChangeEvent<{ value: unknown }>) => {
			if (!onBeforeChange || onBeforeChange(event)) {
				field.onChange(event);
			}
		},
		[field, onBeforeChange],
	);

	React.useEffect(() => {
		if (controllerOptions.defaultValue && !selectValue) {
			field.onChange(controllerOptions.defaultValue);
		}
	}, [controllerOptions.defaultValue, field, selectValue]);

	const handleRenderValue = React.useCallback(() => {
		if (!selectValue && !controllerOptions.defaultValue && placeholder) {
			return <span className={classes.placeholder}>{placeholder}</span>;
		}
		const displayValue = selectValue || controllerOptions.defaultValue;
		const selectedOption = options.find(({ value }) => value === displayValue);
		return selectedOption ? selectedOption.label : '';
	}, [selectValue, controllerOptions.defaultValue, placeholder, options, classes.placeholder]);

	return (
		<StandardInputWrapper<FormType>
			{...statefulProviderProps}
			labelProps={{
				id: `select-label-${statefulProviderProps.name}`,
				htmlFor: `select-field-${statefulProviderProps.name}`,
			}}
			analyticsInputName="dropDown"
		>
			<ChatAwareSelect
				{...field}
				name={statefulProviderProps.name}
				id={`select-field-${statefulProviderProps.name}`}
				labelId={`select-label-${statefulProviderProps.name}`}
				value={selectValue || controllerOptions.defaultValue || ''}
				onChange={handleChange}
				displayEmpty={!!placeholder}
				renderValue={handleRenderValue}
				MenuProps={{
					...MenuProps,
					className: classNames(
						MenuProps?.className,
						classes.fancyScrollOptions
					),
				}}
			>
				{options.map(({ value, label }) => (
					<MenuItem key={value} value={value}>
						{label}
					</MenuItem>
				))}
			</ChatAwareSelect>
		</StandardInputWrapper>
	);
}
